import { Component } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { IconRegistry } from '@intellio/shared/components';
import { filter } from 'rxjs/operators';
import {
  NavigationEnd,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import {
  AppConfigService,
  AuthService,
  SessionStorageService,
} from '@intellio/shared/services';
import { AccountFacade } from '@intellio/shared/state-mgmt';
import { AppConfig } from '@intellio/shared/models';

declare const config: AppConfig;

@Component({
  selector: 'itc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterModule, RouterOutlet],
})
export class AppComponent {
  hasLoggedIn = false;
  /* The app component is only meant as an empty shell for the rest of the application,
  that lives in the connect-root library */
  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private translateService: TranslateService,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private authService: AuthService,
    private accountFacade: AccountFacade,
    private appConfigService: AppConfigService
  ) {
    Object.entries(IconRegistry).forEach(([iconName, fileName]) => {
      this.matIconRegistry.addSvgIcon(
        iconName,
        this.domSanitizer.bypassSecurityTrustResourceUrl(
          `assets/font-svgs/${fileName}.svg`
        )
      );
    });

    this.authService.authToken$.subscribe((token) => {
      if (token) {
        this.hasLoggedIn = true;
      } else if (token === null && this.hasLoggedIn) {
        this.accountFacade.dispatchLogoutAccount();
      }
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        var url = this.router.url;

        // If the url has callback in it and includes an id token
        // this means this url is a callback from the SSO process
        if (
          url.indexOf('callback') !== -1 &&
          (url.indexOf('callback') < url.indexOf('code') || url.indexOf('callback') < url.indexOf('#'))
        ) {
          var code = '';

          // Old SSO url format: <host><route><query-params>
          // New SSO url format: <host><route><query-params><url-fragment>

          if (url.indexOf('#') !== -1) {
            // extract the code after the url fragment
            code = url.split('#')[1];

            // re-assign the url to the old format so the 
            // original code below works as before
            url = url.split('#')[0]; 
          }

          const ssoQueryString = url.split('?')[1];
          const ssoTokens = ssoQueryString.split('&');

          const ssoTokenDictionary = {};

          if(code != ''){
            ssoTokenDictionary['code'] = code;
          }

          for (let i = 0; i < ssoTokens.length; i++) {
            const pair = ssoTokens[i].split('=');
            ssoTokenDictionary[pair[0]] = pair[1];
          }

          if (ssoTokenDictionary['error']) {
            switch (ssoTokenDictionary['error']) {
              case 'invalidAccount':
                this.router.navigate(['sso-invalid-account'], { queryParams: { correlationId: ssoTokenDictionary['correlationId'] }});
                return;

              case 'ssoLoginError':
                this.router.navigate(['sso-login-error']);
                return;
            }
          }

          this.sessionStorageService.set(
            'SSOLogin',
            JSON.stringify(ssoTokenDictionary)
          );
          // clear out the query params from
          // the URL to hide the access code
          this.router.navigate(['callback']);
        }
      });

    this.translateService.setDefaultLang('DistributedGeneration');
    this.loadGoogleAnalytics(this.googleAnalyticsMeasurementId);
  }

  googleAnalyticsMeasurementId: string =
    this.appConfigService.tenantConfig.googleAnalyticsMeasurementId;

  loadGoogleAnalytics(trackingID: string): void {
    window.googleAnalyticsMeasurementId = trackingID;

    let gaScript = document.createElement('script');
    gaScript.setAttribute('async', 'true');
    gaScript.setAttribute(
      'src',
      `https://www.googletagmanager.com/gtag/js?id=${trackingID}`
    );

    let gaScript2 = document.createElement('script');
    gaScript2.setAttribute('src', `assets/google-analytics.js`);

    document.documentElement.firstChild.appendChild(gaScript);
    document.documentElement.firstChild.appendChild(gaScript2);
  }
}
